import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  CountryCode,
  isValidPhoneNumber,
  parsePhoneNumber,
} from "libphonenumber-js/min";
import { getCode } from "country-list";
import { motion } from "framer-motion";
import { MuiTelInput } from "mui-tel-input";
import clsx from "clsx";
import useClickAway from "react-use/lib/useClickAway";

import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import FormHelperText from "@mui/material/FormHelperText";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from "@mui/material/Radio";
import { styled } from "@mui/material/styles";

import BackButton from "../../components/BackButton";
import Curve from "../../components/Animations/PageTransitions/Curve";
import MoveLeft from "../../components/Animations/MoveLeft";
import VirtualKeyboard, {
  LayoutType,
  VirtualKeyboardRef,
} from "../../components/VirtualKeyboard";

import { useLanguage, useLanguages } from "../../bit/language";
import { useDigitalReceptionCountry } from "../../lib/hooks/useQueryBoard";

import { isVirtualKeyboardEnabled } from "../../config/const";

type LocalizedString = Record<string, string>;

interface ClientVisitorFieldOption extends LocalizedString {
  value: string;
}

type ClientVisitorFieldType =
  | "Text"
  | "Email"
  | "Phone"
  | "Number"
  | "Dropdown"
  | "Checkbox"
  | "RadioButtons";

interface ClientVisitorField {
  id: string;
  code: string;
  type: ClientVisitorFieldType;
  required: boolean;
  order: number;
  label: LocalizedString;
  description?: LocalizedString;
  dropdownOptions?: ClientVisitorFieldOption[];
  checkbox?: {
    label: LocalizedString;
    description?: { title?: LocalizedString; text?: LocalizedString };
  };
  radioButtons?: {
    buttons: ClientVisitorFieldOption[];
    hasOther: boolean;
  };
}

interface LocationState {
  fields?: ClientVisitorField[];
  photo?: string | null;
  action?: string;
}

interface FieldValues {
  values: Record<string, string | boolean>;
}

const MissingFieldsGrid = styled(Grid)(({ theme }) => ({
  paddingTop: "3rem",
  flex: "0 1 auto",
  minHeight: 0,
  overflowY: "auto",
  alignContent: "flex-start",
  WebkitOverflowScrolling: "touch",

  "&::-webkit-scrollbar": {
    width: "2rem",
  },

  "&::-webkit-scrollbar-track": {
    background: theme.palette.primary.light,
    borderRadius: "8px",
  },

  "&::-webkit-scrollbar-thumb": {
    background: theme.palette.primary.main,
    borderRadius: "8px",
  },
}));

const MissingFieldsRow = styled(motion.div)(() => ({
  marginBottom: "1.75rem",
  width: "56.5rem",

  "@media screen and (orientation: landscape)": {
    width: "50rem",
  },
}));

const localize = (
  source: LocalizedString | undefined,
  language: string,
): string => {
  if (!source) return "";
  return source[language] || source.en || Object.values(source)[0] || "";
};

const getFieldType = (
  code: string,
  fields: ClientVisitorField[],
): ClientVisitorFieldType | undefined =>
  fields.find((f) => f.code === code)?.type;

const getLayoutForFieldAndValue = (
  layout: LayoutType,
  type: ClientVisitorFieldType | undefined,
  value = "",
): LayoutType => {
  switch (type) {
    case "Text": {
      if (!value || value.slice(-1) === " ") return "default-shift";
      return "default";
    }
    case "Phone":
      return "phone-pad";
    case "Number":
      return "pin";
    case "Email":
      return "email";
    default:
      return layout;
  }
};

const MissingFields = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { code } = useParams<{ code: string }>();
  const { state } = useLocation();

  const {
    fields = [],
    photo = null,
    action = "checkin",
  } = (state as LocationState) || {};

  const [language] = useLanguage();
  const languages = useLanguages();

  const drCountry = useDigitalReceptionCountry();
  const drCountryCode = useMemo(
    () => (drCountry ? getCode(drCountry) : null),
    [drCountry],
  );

  const defaultCountry: CountryCode | undefined = useMemo(() => {
    if (drCountryCode) return drCountryCode as CountryCode;
    if (["nl", "fr", "de", "pt", "es", "pl", "it"].includes(language)) {
      return language.toUpperCase() as CountryCode;
    }
    if (language === "en") return "GB";
    if (language === "en_US") return "US";
    return undefined;
  }, [language, drCountryCode]);

  const preferredCountries = useMemo(
    () =>
      (drCountryCode ? [drCountryCode as CountryCode] : []).concat(
        languages.flatMap((lang) => {
          if (["nl", "fr", "de", "pt", "es", "pl", "it"].includes(lang)) {
            return [lang.toUpperCase()];
          }
          if (lang === "en") return "GB";
          if (lang === "en_US") return ["US"];
          return [];
        }) as CountryCode[],
      ),
    [languages, drCountryCode],
  );

  const sortedFields = useMemo(
    () => [...fields].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [fields],
  );

  const defaultValues: FieldValues = useMemo(
    () => ({
      values: sortedFields.reduce((acc, field) => {
        acc[field.code] = field.type === "Checkbox" ? false : "";
        return acc;
      }, {} as Record<string, string | boolean>),
    }),
    [sortedFields],
  );

  const methods = useForm<FieldValues>({
    defaultValues,
    mode: "onSubmit",
  });

  const { handleSubmit, control, setValue, getValues } = methods;

  const [layout, setLayout] = useState<LayoutType>("default-shift");
  const [focusedInput, setFocusedInput] = useState<string | undefined>(
    sortedFields[0] ? `values.${sortedFields[0].code}` : undefined,
  );

  const [otherByField, setOtherByField] = useState<Record<string, boolean>>({});
  const [customOtherValueByField, setCustomOtherValueByField] = useState<
    Record<string, string>
  >({});

  const keyboard = useRef<VirtualKeyboardRef>(null);
  const keyboardBox = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!sortedFields[0]) return;
    setLayout((prev) =>
      getLayoutForFieldAndValue(prev, sortedFields[0].type, ""),
    );
  }, [sortedFields]);

  useClickAway(keyboardBox, (event) => {
    if (
      event.target &&
      ["INPUT", "TEXTAREA"].includes((event.target as HTMLElement)?.tagName)
    ) {
      return;
    }
    setFocusedInput(undefined);
  });

  const onInputFocus = useCallback(
    (
      event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>,
    ) => {
      const {
        target: { name, value },
      } = event;
      if (!keyboard.current) return;

      const fieldCode = name?.split(".").pop() || "";
      setLayout((prev) =>
        getLayoutForFieldAndValue(
          prev,
          getFieldType(fieldCode, sortedFields),
          value,
        ),
      );
      setFocusedInput(name);
    },
    [sortedFields],
  );

  const onInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const {
        target: { name, value, selectionStart, selectionEnd },
      } = event;
      if (!keyboard.current) return;

      const fieldCode = name?.split(".").pop() || "";
      setLayout((prev) =>
        getLayoutForFieldAndValue(
          prev,
          getFieldType(fieldCode, sortedFields),
          value,
        ),
      );

      keyboard.current.setInput(value, name);
      keyboard.current.setCaretPosition(selectionStart, selectionEnd);
    },
    [sortedFields],
  );

  const onInputSelect = useCallback(
    (event: React.SyntheticEvent<HTMLInputElement, Event>) => {
      if (!keyboard.current) return;
      const { selectionStart, selectionEnd } = event.target as HTMLInputElement;
      if (
        typeof selectionStart !== "number" ||
        typeof selectionEnd !== "number"
      )
        return;
      keyboard.current.setCaretPosition(selectionStart, selectionEnd);
    },
    [],
  );

  const onKeyboardChange = useCallback(
    (value = "") => {
      if (!focusedInput) return;

      const input = document.querySelector<HTMLInputElement>(
        `[name="${focusedInput}"]`,
      );

      let selectionStart = Math.max(0, value.length - 1);
      if (input && typeof input.selectionStart === "number") {
        selectionStart = input.selectionStart;
      }

      const prevValue = (getValues(focusedInput as any) as string) || "";
      const fieldCode = focusedInput.split(".").pop() || "";

      setLayout((prev) =>
        getLayoutForFieldAndValue(
          prev,
          getFieldType(fieldCode, sortedFields),
          value,
        ),
      );

      setValue(focusedInput as any, value);

      if (input) {
        const position = prevValue.length - value.length;
        const next = Math.max(0, selectionStart - position);
        const type = getFieldType(fieldCode, sortedFields);
        if (type === "Phone" || type === "Number") {
          keyboard.current?.setCaretPosition(
            prevValue.length < value.length
              ? prevValue.length + 1
              : value.length,
            prevValue.length < value.length
              ? prevValue.length + 1
              : value.length,
          );
        } else {
          input.setSelectionRange(next, next);
        }
      }
    },
    [focusedInput, sortedFields, getValues, setValue],
  );

  const onKeyboardKeyPress = useCallback((button = "") => {
    if (button === "{alt}") {
      setLayout(
        (l) =>
          (l.endsWith("alt")
            ? l.split("-").shift()
            : `${l.split("-").shift()}-alt`) as LayoutType,
      );
    }
    if (button === "{shift}") {
      setLayout(
        (l) =>
          (l.endsWith("shift")
            ? l.split("-").shift()
            : `${l.split("-").shift()}-shift`) as LayoutType,
      );
    }
  }, []);

  const onSubmit = useCallback(
    ({ values }: FieldValues) => {
      const customFieldValues = Object.fromEntries(
        Object.entries(values).map(([key, raw]) => {
          const field = sortedFields.find((f) => f.code === key);
          if (field?.type === "Phone" && typeof raw === "string" && raw) {
            try {
              return [key, parsePhoneNumber(raw).number];
            } catch {
              return [key, raw];
            }
          }
          return [key, raw];
        }),
      );

      navigate(`/access/${action}/${code}`, {
        replace: true,
        state: { customFieldValues, photo },
      });
    },
    [sortedFields, navigate, action, code, photo],
  );

  if (!code || sortedFields.length === 0) {
    navigate("/", { replace: true });
    return null;
  }

  return (
    <Curve>
      <FormProvider {...methods}>
        <div className="d-flex align-items-center flex-column h-100">
          <BackButton to="/" />
          <div className="mb-5 text-center" style={{ margin: "0 10rem" }}>
            <Typography variant="h1">
              {t("missingFields:title", "A few more details before check-in")}
            </Typography>
            <Typography variant="h3" className="font-weight-regular pt-3">
              {t("missingFields:subtitle", "Fields marked * are required")}
            </Typography>
          </div>
          <form
            className="form d-flex flex-column mt-0"
            style={{ flex: "1 1 0", minHeight: 0, width: "100%" }}
            autoComplete="off"
            onSubmit={handleSubmit(onSubmit)}
          >
            <MissingFieldsGrid container justifyContent="center" columnGap={4}>
              {sortedFields.map((field, i) => {
                const label =
                  localize(field.label, language) +
                  (field.required ? " *" : "");

                const requiredRule = field.required
                  ? { required: t("missingFields:error.required", "Required") }
                  : {};

                if (field.type === "Text" || field.type === "Number") {
                  return (
                    <MoveLeft key={field.code} delay={0.4 + i * 0.06}>
                      <MissingFieldsRow>
                        <Controller
                          name={`values.${field.code}`}
                          control={control}
                          rules={requiredRule}
                          render={({
                            field: { onChange, ref, value, ...rest },
                            fieldState: { error },
                          }) => (
                            <TextField
                              {...rest}
                              error={!!error}
                              helperText={error?.message ?? " "}
                              type={field.type.toLowerCase()}
                              autoFocus={i === 0}
                              inputRef={ref}
                              variant="outlined"
                              label={label}
                              value={value ?? ""}
                              onSelect={onInputSelect}
                              onFocus={onInputFocus}
                              onChange={(e) => {
                                onInputChange(e);
                                onChange(e.target.value);
                              }}
                              className={error ? "error__field" : undefined}
                            />
                          )}
                        />
                      </MissingFieldsRow>
                    </MoveLeft>
                  );
                }

                if (field.type === "Email") {
                  return (
                    <MoveLeft key={field.code} delay={0.4 + i * 0.06}>
                      <MissingFieldsRow>
                        <Controller
                          name={`values.${field.code}`}
                          control={control}
                          rules={{
                            ...requiredRule,
                            pattern: {
                              value:
                                /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/,
                              message: t(
                                "missingFields:error.email",
                                "Invalid email address",
                              ),
                            },
                          }}
                          render={({
                            field: { onChange, ref, value, ...rest },
                            fieldState: { error },
                          }) => (
                            <TextField
                              {...rest}
                              error={!!error}
                              helperText={error?.message ?? " "}
                              inputRef={ref}
                              autoFocus={i === 0}
                              type={isVirtualKeyboardEnabled ? "text" : "email"}
                              variant="outlined"
                              label={label}
                              value={value ?? ""}
                              onSelect={onInputSelect}
                              onFocus={onInputFocus}
                              onChange={(e) => {
                                onInputChange(e);
                                onChange(e.target.value);
                              }}
                              className={error ? "error__field" : undefined}
                            />
                          )}
                        />
                      </MissingFieldsRow>
                    </MoveLeft>
                  );
                }

                if (field.type === "Phone") {
                  return (
                    <MoveLeft key={field.code} delay={0.4 + i * 0.06}>
                      <MissingFieldsRow>
                        <Controller
                          name={`values.${field.code}`}
                          control={control}
                          rules={{
                            ...requiredRule,
                            validate: (value) =>
                              !value ||
                              typeof value !== "string" ||
                              isValidPhoneNumber(value) ||
                              t(
                                "missingFields:error.phone",
                                "Invalid phone number",
                              ),
                          }}
                          render={({
                            field: { ref, onChange, value, ...rest },
                            fieldState: { error },
                          }) => (
                            <MuiTelInput
                              {...rest}
                              error={!!error}
                              inputRef={ref}
                              variant="outlined"
                              autoFocus={i === 0}
                              helperText={error?.message ?? " "}
                              defaultCountry={defaultCountry}
                              preferredCountries={preferredCountries}
                              label={label}
                              value={(value as string) || ""}
                              onSelect={(e: any) => {
                                onInputChange(e);
                                onChange(e.target.value);
                              }}
                              onChange={(input) => onChange(input)}
                              onFocus={onInputFocus}
                              className={error ? "error__field" : undefined}
                            />
                          )}
                        />
                      </MissingFieldsRow>
                    </MoveLeft>
                  );
                }

                if (field.type === "Dropdown") {
                  return (
                    <MoveLeft key={field.code} delay={0.4 + i * 0.06}>
                      <MissingFieldsRow>
                        <Controller
                          name={`values.${field.code}`}
                          control={control}
                          rules={requiredRule}
                          render={({
                            field: { onChange, value },
                            fieldState: { error },
                          }) => {
                            let height = "0";

                            if (field.dropdownOptions?.length) {
                              height = "24rem";

                              if (field.dropdownOptions.length === 2) {
                                height = "17rem";
                              }

                              if (field.dropdownOptions.length === 1) {
                                height = "9rem";
                              }
                            }

                            return (
                              <FormControl fullWidth error={!!error}>
                                <InputLabel>{label}</InputLabel>
                                <Select
                                  value={(value as string) || ""}
                                  onChange={(e) => onChange(e.target.value)}
                                  label={label}
                                  MenuProps={{
                                    PaperProps: {
                                      sx: {
                                        marginTop: "1rem !important",
                                        height,
                                      },
                                    },
                                  }}
                                >
                                  {field.dropdownOptions?.map((option) => (
                                    <MenuItem
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {localize(option, language)}
                                    </MenuItem>
                                  ))}
                                </Select>
                                <FormHelperText>
                                  {error?.message ?? " "}
                                </FormHelperText>
                              </FormControl>
                            );
                          }}
                        />
                      </MissingFieldsRow>
                    </MoveLeft>
                  );
                }

                if (field.type === "RadioButtons") {
                  const OTHER_RADIO_VALUE = "__other__";
                  const isOtherSelected = !!otherByField[field.code];
                  const customOtherValue =
                    customOtherValueByField[field.code] || "";

                  return (
                    <MoveLeft key={field.code} delay={0.4 + i * 0.06}>
                      <MissingFieldsRow>
                        <Controller
                          name={`values.${field.code}`}
                          control={control}
                          rules={requiredRule}
                          render={({
                            field: { onChange, value, name },
                            fieldState: { error },
                          }) => (
                            <FormControl fullWidth error={!!error}>
                              <Typography variant="h4" className="mb-2">
                                {label}
                              </Typography>
                              <RadioGroup
                                name={name}
                                value={
                                  isOtherSelected
                                    ? OTHER_RADIO_VALUE
                                    : value ?? ""
                                }
                                onChange={(e) => {
                                  const v = e.target.value;
                                  if (v === OTHER_RADIO_VALUE) {
                                    setOtherByField((prev) => ({
                                      ...prev,
                                      [field.code]: true,
                                    }));
                                    setCustomOtherValueByField((prev) => ({
                                      ...prev,
                                      [field.code]: "",
                                    }));
                                    onChange("");
                                  } else {
                                    setOtherByField((prev) => ({
                                      ...prev,
                                      [field.code]: false,
                                    }));
                                    setCustomOtherValueByField((prev) => ({
                                      ...prev,
                                      [field.code]: "",
                                    }));
                                    onChange(v);
                                  }
                                }}
                              >
                                {field.radioButtons?.buttons?.map((option) => (
                                  <FormControlLabel
                                    key={option.value}
                                    value={option.value}
                                    control={
                                      <Radio sx={{ transform: "scale(2)" }} />
                                    }
                                    label={
                                      <span
                                        style={{
                                          fontSize: "2.5rem",
                                          marginLeft: "1rem",
                                        }}
                                      >
                                        {localize(option, language)}
                                      </span>
                                    }
                                    style={{
                                      marginLeft: 0,
                                      marginBottom: "0.75rem",
                                    }}
                                  />
                                ))}
                                {field.radioButtons?.hasOther && (
                                  <>
                                    <FormControlLabel
                                      key="other"
                                      value={OTHER_RADIO_VALUE}
                                      control={
                                        <Radio sx={{ transform: "scale(2)" }} />
                                      }
                                      label={
                                        <span
                                          style={{
                                            fontSize: "2.5rem",
                                            marginLeft: "1rem",
                                          }}
                                        >
                                          {t(
                                            "missingFields:placeholder.other",
                                            "Other...",
                                          )}
                                        </span>
                                      }
                                      style={{ marginLeft: 0 }}
                                    />
                                    {isOtherSelected && (
                                      <TextField
                                        fullWidth
                                        variant="outlined"
                                        placeholder={t(
                                          "missingFields:placeholder.otherInput",
                                          "Enter your option",
                                        )}
                                        value={customOtherValue}
                                        onChange={(e) => {
                                          setCustomOtherValueByField(
                                            (prev) => ({
                                              ...prev,
                                              [field.code]: e.target.value,
                                            }),
                                          );
                                          onChange(e.target.value);
                                        }}
                                        style={{
                                          marginTop: "1rem",
                                          fontSize: "2rem",
                                        }}
                                      />
                                    )}
                                  </>
                                )}
                              </RadioGroup>
                              <FormHelperText>
                                {error?.message ?? " "}
                              </FormHelperText>
                            </FormControl>
                          )}
                        />
                      </MissingFieldsRow>
                    </MoveLeft>
                  );
                }

                if (field.type === "Checkbox") {
                  const checkboxLabel = localize(
                    field.checkbox?.label,
                    language,
                  );
                  const descTitle = localize(
                    field.checkbox?.description?.title,
                    language,
                  );
                  const descText = localize(
                    field.checkbox?.description?.text,
                    language,
                  );

                  return (
                    <MoveLeft key={field.code} delay={0.4 + i * 0.06}>
                      <MissingFieldsRow>
                        <Typography variant="h4">{label}</Typography>
                        {descTitle && (
                          <Typography variant="h4" className="fs-3">
                            {descTitle}
                          </Typography>
                        )}
                        {descText && (
                          <p style={{ fontSize: "1.85rem" }}>{descText}</p>
                        )}
                        <Controller
                          name={`values.${field.code}`}
                          control={control}
                          rules={
                            field.required
                              ? {
                                  validate: (v) =>
                                    v === true ||
                                    t(
                                      "missingFields:error.required",
                                      "Required",
                                    ),
                                }
                              : {}
                          }
                          render={({
                            field: { onChange, value, ...rest },
                            fieldState: { error },
                          }) => (
                            <FormControl error={!!error}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    {...rest}
                                    checked={!!value}
                                    onChange={(e) => onChange(e.target.checked)}
                                    sx={{
                                      "& .MuiSvgIcon-root": {
                                        fontSize: "3rem",
                                      },
                                    }}
                                  />
                                }
                                label={checkboxLabel}
                                sx={{
                                  marginBottom: "1rem",
                                  "& .MuiFormControlLabel-label": {
                                    fontSize: "2.5rem",
                                  },
                                }}
                              />
                              <FormHelperText>
                                {error?.message ?? " "}
                              </FormHelperText>
                            </FormControl>
                          )}
                        />
                      </MissingFieldsRow>
                    </MoveLeft>
                  );
                }

                return null;
              })}
            </MissingFieldsGrid>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <MoveLeft delay={0.6}>
                <motion.div
                  className="mt-5"
                  style={{
                    width: "56.5rem",
                    paddingBottom: "5rem",
                  }}
                >
                  <Button
                    variant="contained"
                    className="w-100"
                    style={{
                      transform: "translateX(-1rem)",
                    }}
                    type="submit"
                  >
                    {t("missingFields:btn", "Continue check-in")}
                  </Button>
                </motion.div>
              </MoveLeft>
            </div>
          </form>
          {isVirtualKeyboardEnabled && (
            <div
              ref={keyboardBox}
              className={clsx("keyboard ", { keyboard__out: !focusedInput })}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <VirtualKeyboard
                ref={keyboard}
                layoutName={layout}
                inputName={focusedInput}
                onChange={onKeyboardChange}
                onKeyPress={onKeyboardKeyPress}
              />
            </div>
          )}
        </div>
      </FormProvider>
    </Curve>
  );
};

export default MissingFields;
